"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { Search, X } from "@/components/icons";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { buildTradeStatePath } from "@/lib/routes/trade";
import { IconButton } from "@/components/ui/icon-button";
import {
  filterStatesByQuery,
  groupStatesByRegion,
  type StateItem,
} from "./states-grid-regions";

interface StatesGridProps {
  states: StateItem[];
  productType?: string;
  showSearch?: boolean;
  showListingCounts?: boolean;
}

type StateLinkOptions = {
  productType?: string;
  showListingCount?: boolean;
};

function useStatesSearch(states: StateItem[]) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredStates = useMemo(
    () => filterStatesByQuery(states, searchQuery),
    [states, searchQuery],
  );

  const hiddenStates = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return states.filter((state) => !filteredStates.includes(state));
  }, [states, filteredStates, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  return { searchQuery, setSearchQuery, filteredStates, hiddenStates, isSearching };
}

function StatesSearchField({
  searchQuery,
  onSearchQueryChange,
}: {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}) {
  return (
    <div className="relative w-full max-w-xs">
      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="text"
        placeholder="Filter states..."
        value={searchQuery}
        onChange={(event) => onSearchQueryChange(event.target.value)}
        className="pl-9 pr-8"
        aria-label="Filter states by name or code" size="sm"
      />
      {searchQuery && (
        <IconButton
          size="inline"
          onClick={() => onSearchQueryChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
          aria-label="Clear search"
        >
          <X />
        </IconButton>
      )}
    </div>
  );
}

function StatesGridHeader({
  stateCount,
  showSearch,
  searchQuery,
  onSearchQueryChange,
}: {
  stateCount: number;
  showSearch: boolean;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}) {
  return (
    <div className="mb-6 flex items-center justify-between gap-4">
      <span className="text-xs font-semibold text-muted-foreground">
        {stateCount} States
      </span>

      {showSearch && (
        <StatesSearchField
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
        />
      )}
    </div>
  );
}

function StateRegionColumn({
  heading,
  states,
  linkOptions,
}: {
  heading: string;
  states: StateItem[];
  linkOptions: StateLinkOptions;
}) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold text-muted-foreground">
        {heading}
      </h3>
      <div className="space-y-1">
        {states.map((state) => (
          <StateLink key={state.code} state={state} {...linkOptions} />
        ))}
      </div>
    </div>
  );
}

function StatesByRegion({
  states,
  linkOptions,
}: {
  states: StateItem[];
  linkOptions: StateLinkOptions;
}) {
  const { groupedStates, unmappedStates } = useMemo(
    () => groupStatesByRegion(states),
    [states],
  );
  return (
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {groupedStates.map((group) => (
        <StateRegionColumn
          key={group.region}
          heading={group.region}
          states={group.states}
          linkOptions={linkOptions}
        />
      ))}

      {unmappedStates.length > 0 && (
        <StateRegionColumn
          key="Other"
          heading="Other"
          states={unmappedStates}
          linkOptions={linkOptions}
        />
      )}
    </div>
  );
}

export function StatesGrid({
  states,
  productType,
  showSearch = true,
  showListingCounts = true,
}: StatesGridProps) {
  const search = useStatesSearch(states);
  const { searchQuery, filteredStates, isSearching } = search;
  const linkOptions = { productType, showListingCount: showListingCounts };

  return (
    <Card className="bg-muted/30" padding="roomy">
      <StatesGridHeader
        stateCount={states.length}
        showSearch={showSearch}
        searchQuery={searchQuery}
        onSearchQueryChange={search.setSearchQuery}
      />

      {isSearching ? (
        <div className="columns-2 gap-x-6 sm:columns-3 md:columns-4 lg:columns-6">
          {filteredStates.map((state) => (
            <StateLink key={state.code} state={state} {...linkOptions} />
          ))}
        </div>
      ) : (
        <StatesByRegion states={filteredStates} linkOptions={linkOptions} />
      )}

      {search.hiddenStates.map((state) => (
        <StateLink
          key={state.code}
          state={state}
          productType={productType}
          showListingCount={false}
          className="sr-only"
        />
      ))}

      {isSearching && filteredStates.length === 0 && (
        <p className="py-4 text-center text-sm text-muted-foreground">
          No states match &ldquo;{searchQuery}&rdquo;
        </p>
      )}
    </Card>
  );
}

function StateLink({
  state,
  productType,
  showListingCount,
  className,
}: {
  state: StateItem;
  productType?: string;
  showListingCount?: boolean;
  className?: string;
}) {
  const href = productType
    ? buildTradeStatePath(`buy-${productType}`, state.slug)
    : "/listing";

  return (
    <Link
      href={href}
      prefetch={false}
      className={cn(
        "block py-0.5 text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline",
        className,
      )}
    >
      {state.name}
      {showListingCount && state.listingCount > 0 && (
        <span className="ml-1 text-xs opacity-60">
          ({state.listingCount}{state.listingCountIsCapped ? "+" : ""})
        </span>
      )}
    </Link>
  );
}
